import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Mail, Download } from 'lucide-react';
import * as Icons from 'lucide-react';

export function About() {
  const { data: portfolioData, isLoading } = usePortfolioData();

  const DynamicIcon = ({ name }: { name: string }) => {
    const LucideIcon = (Icons as any)[name] || Icons.Circle;
    return <LucideIcon className="text-primary h-5 w-5" />;
  };

  if (isLoading) {
    return (
      <section id="about" className="py-24 bg-white dark:bg-slate-900/60">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Skeleton className="text-3xl font-bold mb-4 h-12 w-48 mx-auto" />
            <Skeleton className="w-20 h-2 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Skeleton className="rounded-2xl h-96 w-full" />

            <div>
              <Skeleton className="text-2xl font-semibold mb-4 h-10 w-60" />
              <div className="space-y-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
              <div className="mt-10 grid grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-start">
                    <Skeleton className="h-8 w-8 mt-1 mr-3 rounded-lg" />
                    <div>
                      <Skeleton className="h-5 w-24 mb-2" />
                      <Skeleton className="h-5 w-40" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!portfolioData) return null;

  return (
    <section id="about" className="py-24 relative">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background"></div>

      {/* Background decorations */}
      <div className="absolute left-0 top-1/4 w-64 h-64 bg-primary/5 rounded-full filter blur-3xl"></div>
      <div className="absolute right-0 bottom-1/4 w-64 h-64 bg-secondary/5 rounded-full filter blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <span className="animate-pulse mr-2">✦</span> About Me
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center tracking-tight">
            Get to <span className="bg-gradient-text">Know Me Better</span>
          </h2>
          <div className="w-20 h-1.5 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative animate-fade-in">
            {/* Decoration */}
            <div className="absolute -left-6 -top-6 w-12 h-12 border-t-4 border-l-4 border-primary opacity-60 hidden md:block"></div>
            <div className="absolute -right-6 -bottom-6 w-12 h-12 border-b-4 border-r-4 border-secondary opacity-60 hidden md:block"></div>

            {/* Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl card-hover w-full h-80 md:h-96">
              <div className="relative w-full h-full">
                <img
                  src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                  alt="Software developer working on laptop"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500 ease-in-out"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';
                  }}
                />

                {/* Stats overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex space-x-4">
                  <div className="flex-1 bg-background/80 backdrop-blur-sm p-4 rounded-xl shadow-lg">
                    <div className="text-3xl font-bold text-primary">2+</div>
                    <div className="text-sm text-muted-foreground">Years Experience</div>
                  </div>
                  <div className="flex-1 bg-background/80 backdrop-blur-sm p-4 rounded-xl shadow-lg">
                    <div className="text-3xl font-bold text-secondary">5+</div>
                    <div className="text-sm text-muted-foreground">Projects</div>
                  </div>
                  <div className="flex-1 bg-background/80 backdrop-blur-sm p-4 rounded-xl shadow-lg">
                    <div className="text-3xl font-bold text-accent">10+</div>
                    <div className="text-sm text-muted-foreground">Technologies</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8 animate-slide-up animate-delay-100">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-1 bg-primary rounded-full"></div>
              <h3 className="text-3xl font-bold tracking-tight">Hi, I'm {portfolioData.name}</h3>
            </div>

            <div
              className="prose prose-lg dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: portfolioData.bio }}
            />

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {portfolioData.keyDetails.map((detail, index) => (
                <div
                  key={detail.label}
                  className="flex items-start bg-muted/50 p-4 rounded-xl hover:bg-primary/5 transition-colors duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="mr-4 p-2 bg-primary/10 rounded-lg text-primary">
                    <DynamicIcon name={detail.icon} />
                  </div>
                  <div>
                    <h4 className="font-medium text-muted-foreground">{detail.label}</h4>
                    <p className="font-semibold">{detail.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <a href="#contact" className="inline-block">
                <Button className="gap-2 rounded-xl" variant="outline">
                  <Mail className="w-4 h-4" /> Get in Touch
                </Button>
              </a>
              <a href={portfolioData.resumeUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                <Button className="gap-2 rounded-xl" variant="default">
                  <Download className="w-4 h-4" /> Download CV
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
